import React, { useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { IconButton, WasteIcon } from '@hvcgroep/design-system';
import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { Map } from 'leaflet';

import './style.scss';
import { Container } from '../../types';
import normalizeContainerType from '../../helpers/normalizeContainerType';
import addressMapsLink from '../../helpers/addressMapsLink';

const popupWrapperId = 'leaflet-marker-container';

type Props = {
  container: {
    data: Container;
  };
};

export const LeafletPopup: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const {
    container,
  } = props;

  let address;

  if ('container_id' in container.data) {
    [, address] = addressMapsLink(container.data.street, container.data.house_number, container.data.locality);
  }

  return (
    <div id={popupWrapperId}>
      <div className="LeafletPopup">
        <IconButton icon="close" variant="white">
          <span className="icon icon-blob" />
        </IconButton>
        <div className="LeafletPopup__header">
          <WasteIcon streamUid={normalizeContainerType(container.data.type)} />
          <h5 className="m-0 mb-1 p">
            {'container_id' in container.data ? (
              <>{t(`container_locations.popup.title.${normalizeContainerType(container.data.type)}`)}</>
            ) : (
              <>{container.data.title}</>
            )}
          </h5>
        </div>
        <div className="LeafletPopup__content">
          {'container_id' in container.data && (
            <>
              <p className="text-bold my-0 u-lowercase">{t('container_locations.container_list.address')}</p>
              <p className="my-0 mb-4">{ address }</p>
              <p className="text-bold my-0 u-lowercase">{t('container_locations.container_list.container_number')}</p>
              <p className="my-0">{ container.data.container_id }</p>
            </>
          )}
          {'body' in container.data && (
            <WysiwygField
              html={`<div>${container.data.body}</div>`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const LeafletPopupContent: React.FC<{ mapRef: React.RefObject<Map>; }> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { mapRef } = props;

  useEffect(() => {
    const popup = document.getElementById(popupWrapperId)?.innerHTML || '';
    if (ref.current) {
      ref.current.innerHTML = popup;
    }

    return document.querySelectorAll('.LeafletPopup button')?.[1]?.addEventListener('click', () => {
      mapRef.current?.closePopup();
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        mapRef.current?.closePopup();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div ref={ref} />
  );
};

export const popupContentConstructor = (mapRef: React.RefObject<Map>) => {
  const div = document.createElement('div');
  const root = createRoot(div);

  root.render(
    <LeafletPopupContent mapRef={mapRef} />,
  );

  return div;
};
